import type {ReactElement} from 'react';
import React, {useRef} from 'react';
import BaseNavOption from '@Components/poster-editor/components/poster-editor-nav-bar/components/base-nav-option/base-nav-option';
import styles from './nav-save-option.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {isEmbeddedMorphModeActive} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.helper';
import {NavRightSideOptionTypes} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-right-options/nav-right-options.types';
import {isInReviewOrGalleryTemplate} from '@Components/poster-editor/library/poster-editor-library';
import {UserSaveConflictPopup} from '@Components/user-save-conflict-popup';
import {EditorType} from '@Utils/misc.util';
import {updateSaveConflictDataDetails} from '@Components/poster-editor/poster-editor-reducer';

interface NavSaveOptionProps {
  saveOptionType?: NavRightSideOptionTypes;
}

export function NavSaveOption({saveOptionType = NavRightSideOptionTypes.BUTTON}: NavSaveOptionProps): ReactElement {
  const didPosterRecentlySave = useAppSelector((state) => {
    return state.posterEditor.didPosterRecentlySave;
  });
  const isEmbeddedExportMode = useAppSelector((state) => {
    return state.posterEditor.isEmbeddedExportMode;
  });
  const areImagesUploading = useAppSelector((state) => {
    return !!state.posterEditor.posterObject?.uploadingImages.length;
  });
  const hasUnsavedChanges = useAppSelector((state) => {
    return state.posterEditor.hasUnsavedChanges;
  });
  const containerRef = useRef<HTMLElement>(null);
  const saveConflictData = useAppSelector((state) => {
    return state.posterEditor.saveConflictData;
  });
  const dispatch = useAppDispatch();

  const getSaveIcon = (): string => {
    if (didPosterRecentlySave) {
      return 'icon-check';
    }

    if (isInReviewOrGalleryTemplate()) {
      return 'icon-submit';
    }

    return 'icon-save';
  };

  const getSaveText = (): string => {
    if (didPosterRecentlySave) {
      return window.i18next.t('pmwjs_saved');
    }

    if (isInReviewOrGalleryTemplate()) {
      return window.i18next.t('pmwjs_save_&_submit');
    }

    return window.i18next.t('pmwjs_save');
  };

  const shouldHideSaveOption = (): boolean => {
    return isEmbeddedExportMode || !!window.posterEditor?.whiteboard?.savePoster.isAutoSaveEnabled() || isEmbeddedMorphModeActive();
  };

  const onSave = (): void => {
    if (didPosterRecentlySave) {
      return;
    }

    void window.posterEditor?.whiteboard?.savePoster.save();
  };

  const getSaveDisabledState = (): boolean => {
    if (didPosterRecentlySave) {
      return false;
    }

    return !hasUnsavedChanges || areImagesUploading;
  };

  const isPosterSaving = useAppSelector((state) => {
    return state.posterEditor.isPosterSaving;
  });

  const closePopup = (): void => {
    dispatch(updateSaveConflictDataDetails(undefined));
  };

  const getPopup = (): ReactElement | null => {
    const GAP = 8;
    if (!containerRef.current || !saveConflictData) {
      return null;
    }

    if (!saveConflictData.triggerOpenSaveConflictPopup) {
      return null;
    }

    return (
      <div
        style={{
          top: containerRef.current.getBoundingClientRect().bottom + GAP,
          right: window.innerWidth - containerRef.current.getBoundingClientRect().right,
        }}
        className={styles.popup}
      >
        <UserSaveConflictPopup
          currentUserPictureURL={saveConflictData.currentUserPictureURL}
          otherUserPictureURL={saveConflictData.otherUserPictureURL}
          otherUserName={saveConflictData.otherUserName}
          editorType={EditorType.DESIGN}
          onAcceptYours={(): void => {
            saveConflictData.onAcceptYours();
            closePopup();
          }}
          onAcceptTheirs={(): void => {
            saveConflictData.onAcceptTheirs();
            closePopup();
          }}
        />
      </div>
    );
  };

  const showSaveOption = (): ReactElement | null => {
    return shouldHideSaveOption() ? null : (
      <BaseNavOption
        ref={containerRef}
        id="nav-save-option"
        icon={getSaveIcon()}
        text={getSaveText()}
        optionType={saveOptionType}
        onClickHandler={(): void => {
          onSave();
        }}
        hasMarginRight
        disabled={getSaveDisabledState()}
        isLoading={isPosterSaving}
        btnClasses={`${styles.saveButton} ${isInReviewOrGalleryTemplate() ? styles.saveAndSubmitButton : ''}`}
      />
    );
  };

  return (
    <>
      {showSaveOption()}
      {getPopup()}
    </>
  );
}
